/** Date helpers for the agenda calendar (Monday-first week). */

const NL_MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

const NL_WEEKDAYS_SHORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

export function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseDateTime(s: string): Date {
  return new Date(s.includes("T") ? s : s.replace(" ", "T"));
}

export function getMonthName(monthIndex: number): string {
  return NL_MONTHS[monthIndex] ?? "";
}

export function getWeekdayShort(d: Date): string {
  const js = d.getDay();
  const idx = js === 0 ? 6 : js - 1;
  return NL_WEEKDAYS_SHORT[idx] ?? "";
}

/** Monday of the week containing `d`. */
export function getWeekMonday(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Inclusive date keys from start to end (calendar dates only). */
export function dateKeysBetween(start: Date, end: Date): string[] {
  const keys: string[] = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last) {
    keys.push(formatDateKey(cur));
    cur = addDays(cur, 1);
  }
  return keys;
}

/** Grid cells for month view: 42 days, Monday-first, including prev/next month padding. */
export function getMonthGridCells(anchor: Date): { date: Date; dateKey: string; inMonth: boolean }[] {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const jsDay = first.getDay();
  const adjustedFirst = jsDay === 0 ? 6 : jsDay - 1;
  const start = addDays(first, -adjustedFirst);
  const cells: { date: Date; dateKey: string; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(start, i);
    cells.push({
      date,
      dateKey: formatDateKey(date),
      inMonth: date.getMonth() === month,
    });
  }
  return cells;
}

export function getVisibleRange(
  anchor: Date,
  viewMode: "month" | "week" | "list"
): { from: string; to: string } {
  if (viewMode === "week") {
    const mon = getWeekMonday(anchor);
    const sun = addDays(mon, 6);
    return { from: formatDateKey(mon), to: formatDateKey(sun) };
  }
  const cells = getMonthGridCells(anchor);
  return { from: cells[0]!.dateKey, to: cells[cells.length - 1]!.dateKey };
}

export function formatTimeNl(dt: string): string {
  const d = parseDateTime(dt);
  return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

export function formatTimeRange(start: string, end: string): string {
  return `${formatTimeNl(start)} – ${formatTimeNl(end)}`;
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function itemTouchesDateKey(
  startDatetime: string,
  endDatetime: string,
  dateKey: string
): boolean {
  const dayStart = new Date(`${dateKey}T00:00:00`);
  const dayEnd = new Date(`${dateKey}T23:59:59`);
  const start = parseDateTime(startDatetime);
  const end = parseDateTime(endDatetime);
  return start <= dayEnd && end >= dayStart;
}
